import { record, scalar, upper, type Row, type CampaignClock } from './context.js';

/** `captured` (coordinator Round 30 P2 #4, lane A13): a turn-start scope the observer captured when it SCHEDULED its debounce. If the turn
 *  moved on inside the window, the encounter GM's page (which the commit's binding accepts at any time in combat) still finishes that
 *  holder's START-time due activity instead of a round late. Pin its clock before debounce/queue waits, never at END.
 *  Other pages keep the holder rule; captured scopes cannot cross combat identities or a rolled-back start round. */
export type TimedFinishScope = { kind: 'turn-start'; holderId: string; captured?: boolean; startClock?: CampaignClock; combatId?: string; startRound?: number; pending?: boolean } | { kind: 'push' } | { kind: 'ooc' };
/** No encounter means no timed action (including equipment: the current finish commit requires one). */
export function timedFinishScopeFor(encounter: Row | null, push = false): TimedFinishScope | null {
  if (!encounter) return null;
  const b = record(encounter.encounters) ?? encounter;
  if (Number(scalar(b.in_combat)) !== 1) return { kind: 'ooc' };
  if (push) return { kind: 'push' };
  if (String(scalar(b.turn_phase) ?? 'main') !== 'main') return null;
  const round = scalar(b.current_round);
  return { kind: 'turn-start', holderId: upper(b.current_encounter_creature_id_turn), combatId: upper(b.active_combat_id),
    ...(round != null && Number.isSafeInteger(Number(round)) ? { startRound: Number(round) } : {}) };
}
export function timedFinishScopeHolds(encounter: Row | null, scope: TimedFinishScope, isGM: boolean): boolean {
  if (!encounter) return false;
  const b = record(encounter.encounters) ?? encounter, combat = Number(scalar(b.in_combat)) === 1;
  if (scope.kind === 'ooc') return !combat;
  if (!combat) return false;
  if (scope.kind === 'push') return isGM;
  const round = scalar(b.current_round);
  if (scope.pending || (scope.startRound !== undefined && (!Number.isSafeInteger(scope.startRound)
      || round == null || !Number.isSafeInteger(Number(round)) || Number(round) < scope.startRound))) return false;
  if (scope.combatId !== undefined && upper(b.active_combat_id) !== upper(scope.combatId)) return false;
  if (scope.captured && (!scope.startClock || scope.combatId === undefined || scope.startRound === undefined)) return false;
  if (scope.captured && isGM) return true;
  return upper(b.current_encounter_creature_id_turn) === upper(scope.holderId) && String(scalar(b.turn_phase) ?? 'main') === 'main';
}
export const timedFinishOwnerIds = (scope: TimedFinishScope): readonly string[] | 'all' => scope.kind === 'turn-start' ? [scope.holderId] : 'all';
